import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Inbox, MessagesSquare, BellDot, Star } from "lucide-react";
import api from "../../lib/api";

export default function BusinessDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/business/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-neutral-400" data-testid="overview-loading">Loading…</p>;

  const stats = [
    { label: "Forms", value: data.forms, icon: FileText, to: "/dashboard/forms", testid: "stat-forms" },
    { label: "Submissions this month", value: data.submissions_month, icon: Inbox, to: "/dashboard/submissions", testid: "stat-submissions" },
    { label: "Conversations", value: data.conversations, icon: MessagesSquare, to: "/dashboard/conversations", testid: "stat-conversations" },
    { label: "Unread replies", value: data.unread, icon: BellDot, to: "/dashboard/conversations", testid: "stat-unread" },
    { label: "Avg rating", value: data.rating_avg ? `${data.rating_avg} (${data.rating_count})` : "—", icon: Star, to: "/dashboard/customers", testid: "stat-rating" },
  ];

  return (
    <div data-testid="business-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black" data-testid="dashboard-title">Hi, {data.business?.owner_name || data.business?.name}</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Plan: <b data-testid="dashboard-plan-name">{data.plan?.name || "None"}</b>
            {data.business?.fees_waived && <span className="ml-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-xs">fees waived</span>}
            {" "}· {data.submissions_month} / {(data.plan?.features?.max_submissions_month || 0).toLocaleString()} submissions used this month
          </p>
        </div>
        <Link to="/dashboard/forms/new" data-testid="new-form-cta">
          <button className="rounded-full bg-neutral-900 text-white text-sm px-5 py-2.5 transition-transform duration-150 hover:-translate-y-0.5">
            + New form
          </button>
        </Link>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={s.testid} className="bg-white border border-[#E5E5E5] rounded-2xl p-5 hover:shadow-sm transition-shadow duration-200">
            <s.icon className="h-5 w-5 text-neutral-400" />
            <div className="mt-4 text-3xl font-black font-heading">{s.value}</div>
            <div className="text-xs text-neutral-500 mt-1">{s.label}</div>
          </Link>
        ))}
      </div>

      <h2 className="mt-12 text-base md:text-lg font-bold" data-testid="recent-submissions-heading">Recent submissions</h2>
      <div className="mt-4 bg-white border border-[#E5E5E5] rounded-2xl divide-y divide-[#E5E5E5]">
        {data.recent_submissions.length === 0 && (
          <p className="p-6 text-sm text-neutral-400" data-testid="recent-submissions-empty">
            No submissions yet. Share your form link to start receiving customers.
          </p>
        )}
        {data.recent_submissions.map((s) => (
          <div key={s.id} className="p-5 flex items-center justify-between gap-4" data-testid={`recent-submission-${s.id}`}>
            <div className="min-w-0">
              <div className="font-medium truncate">{s.customer_name || s.customer_email}</div>
              <div className="text-xs text-neutral-400">{s.form_title} · {s.files?.length || 0} file(s)</div>
            </div>
            <span className="text-xs text-neutral-400 shrink-0">{new Date(s.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
